import { Component, inject, Input, OnInit } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { EcoScoreService } from 'src/app/shared/services/eco-score.service';

@Component({
  selector: 'app-intensity-gauge',
  templateUrl: './intensity-gauge.component.html',
  styleUrl: './intensity-gauge.component.scss',
  imports: [ChartModule],
})
export class IntensityGaugeComponent implements OnInit {
  @Input() carbonIntensity: number = 0;

  private ecoScoreService = inject(EcoScoreService);

  getEcoScore(): number {
    return this.ecoScoreService.getEcoScore(this.carbonIntensity);
  }

  color: string[] = [];
  data = {};
  options = {};

  gaugeNeedle = {
    id: 'gaugeNeedle',
    // Si j'utilise le type Chart mis à disposition par Chart.js il ne reconnait pas les propriété innerRadius et outerRadius.
    // J'ai essayé de me plonger dans la doc de Chart.js, mais je ne trouve pas la réponse.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    afterDatasetsDraw: (chart: any) => {
      const { ctx } = chart;
      ctx.save();

      const color = this.color[this.getEcoScore() - 1];

      const xCenter = chart.getDatasetMeta(0).data[0].x;
      const yCenter = chart.getDatasetMeta(0).data[0].y;
      const innerRadius = chart.getDatasetMeta(0).data[0].innerRadius;
      const outerRadius = chart.getDatasetMeta(0).data[0].outerRadius;

      const needleLength = outerRadius - (outerRadius - innerRadius) / 2;
      const baseWidth = 16;

      ctx.translate(xCenter, yCenter);

      const meter = this.getEcoScore() * 2 * (Math.PI / 20) - Math.PI / 20;
      ctx.rotate(Math.PI + Math.PI / 2 + meter);

      // Needle
      ctx.beginPath();
      ctx.moveTo(-baseWidth / 2, 0);
      ctx.lineTo(0, -needleLength);
      ctx.lineTo(baseWidth / 2, 0);
      ctx.fillStyle = color;
      ctx.fill();

      // Base arrondie
      ctx.beginPath();
      ctx.arc(0, 0, baseWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    },
  };
  plugins = [this.gaugeNeedle];

  ngOnInit(): void {
    const rootStyle = getComputedStyle(document.documentElement);
    this.color = [
      rootStyle.getPropertyValue('--color-dark-red').trim(),
      rootStyle.getPropertyValue('--color-bright-red').trim(),
      rootStyle.getPropertyValue('--color-dark-orange').trim(),
      rootStyle.getPropertyValue('--color-bright-orange').trim(),
      rootStyle.getPropertyValue('--color-yellow').trim(),
      rootStyle.getPropertyValue('--color-yellow-green').trim(),
      rootStyle.getPropertyValue('--color-primary-100').trim(),
      rootStyle.getPropertyValue('--color-teal').trim(),
      rootStyle.getPropertyValue('--color-bright-green').trim(),
      rootStyle.getPropertyValue('--color-dark-green').trim(),
    ];

    this.data = {
      labels: [
        '>400 kg CO2e',
        '>300 kg CO2e',
        '>200 kg CO2e',
        '>150 kg CO2e',
        '>100 kg CO2e',
        '>45 kg CO2e',
        '<45 kg CO2e',
        '<30 kg CO2e',
        '<20 kg CO2e',
        '<10 kg CO2e',
      ],

      datasets: [
        {
          data: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
          backgroundColor: this.color,
          borderWidth: 0,
          circumference: 180,
          rotation: -90,
          cutout: '70%',
        },
      ],
    };
    this.options = {
      aspectRatio: 1.7,
      responsive: true,
      maintainAspectRatio: false, // Permet de mieux gérer la hauteur
      layout: {
        padding: {
          top: 5, // Ajoute un espace en haut pour pousser le graphique vers le bas
          bottom: 5, // Supprime l’espace en bas
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            // Personnalise le texte du tooltip
            label: (context: { label: string }) => {
              return context.label; // Retourne uniquement le label associé
            },
            // Supprime le titre (optionnel)
            title: () => {
              return ''; // Retourne une chaîne vide si aucun titre n'est souhaité
            },
          },
        },
      },
    };
  }
}
